import FormFieldController from "confinus_ui/forms/form_field_controller";
import { showConfirmModal } from "lib/overlay/modal";
import { markAsSafeHTML } from "lib/util/safe_html";
import { ModalLifecycleEvent } from "confinus_ui/modal_controller";
import FormController from "confinus_ui/forms/form_controller";

export default class SwitchController extends FormFieldController {
  public static values = {
    ...FormFieldController.values,
    confirmOnSubmit: { type: String, default: null },
    confirmOnSubmitYes: { type: String, default: null },
    onConfirmOnSubmitNo: { type: String, default: null },
    submitOnChange: Boolean,
  };

  declare private readonly confirmOnSubmitValue?: string;
  declare private readonly hasConfirmOnSubmitValue: boolean;
  declare private readonly confirmOnSubmitYesValue?: string;
  declare private readonly confirmOnSubmitNoValue?: string;
  declare private readonly submitOnChangeValue?: boolean;

  public static targets = FormFieldController.targets.concat([
    "switch",
    "slider",
    "sliderStart",
    "sliderEnd",
  ]);

  declare protected readonly switchTarget: HTMLButtonElement;
  declare protected readonly sliderTarget: HTMLSpanElement;
  declare protected readonly sliderStartTarget: HTMLSpanElement;
  declare protected readonly sliderEndTarget: HTMLSpanElement;

  public static classes = [
    "switchOn",
    "switchOff",
    "sliderOn",
    "sliderOff",
    "sliderStartOn",
    "sliderStartOff",
    "sliderEndOff",
    "sliderEndOn",
  ];

  declare protected readonly sliderEndOffClasses: string[];
  declare protected readonly sliderEndOnClasses: string[];
  declare protected readonly sliderStartOffClasses: string[];
  declare protected readonly sliderStartOnClasses: string[];
  declare protected readonly sliderOffClasses: string[];
  declare protected readonly sliderOnClasses: string[];
  declare protected readonly switchOffClasses: string[];
  declare protected readonly switchOnClasses: string[];

  public handleClick(event: Event) {
    event.preventDefault();
    if (!this.inputTarget.disabled) {
      this.toggleSwitch();
    }
  }

  public handleChange() {
    if (this.submitOnChangeValue) {
      if (!this.confirmOnSubmitValue) {
        // We can submit this right away.
        this.submitForm();
        return;
      }
      // Check if the form is valid - if it is, pop open a confirmation dialog.
      this.dispatch("validate", {
        prefix: FormController.identifier,
        target: this.element,
        bubbles: true,
        cancelable: false,
        detail: {
          onValidated: this.handleFormValidatedBeforeConfirm,
        },
      });
    }
  }

  private handleFormValidatedBeforeConfirm = (isFormValid: boolean) => {
    if (isFormValid) {
      // Show the confirmation dialog, using the text sent in via data attributes.
      const confirmMessage = this.confirmOnSubmitValue!;

      // TODO i18n
      const confirmLabel = this.confirmOnSubmitYesValue || "Confirm";
      const cancelLabel = this.confirmOnSubmitNoValue || "Cancel";

      showConfirmModal({
        defaultReason: "cancel",
        negativeButtonReason: "cancel",
        negativeButtonLabel: cancelLabel,
        positiveButtonReason: "confirm",
        positiveButtonLabel: confirmLabel,
        message: confirmMessage,
        onClosing: this.handleDialogClosing,
      });
    } else {
      this.toggleSwitchWithoutChangeEvent();
    }
  };

  private handleDialogClosing = ({
    detail: { closeReason },
  }: ModalLifecycleEvent) => {
    if (closeReason === "confirm") {
      // Submit the form.
      this.submitForm();
    } else {
      this.toggleSwitchWithoutChangeEvent();
    }
  };

  private submitForm = () => {
    // Ask the parent controller to submit the form.
    this.dispatch("submit", {
      prefix: FormController.identifier,
      target: this.element,
      bubbles: true,
      cancelable: false,
      detail: {
        onSubmissionPrevented: this.toggleSwitchWithoutChangeEvent,
      },
    });
  };

  public toggleSwitch = () => {
    this.toggleSwitchState();
    this.toggleSwitchView();
  };

  public toggleSwitchWithoutChangeEvent = () => {
    this.toggleSwitchState(false);
    this.toggleSwitchView();
  };

  private toggleSwitchState(withChangeEvent = true) {
    this.inputTarget.checked = !this.inputTarget.checked;

    this.validate();
    if (withChangeEvent) this.handleChange();
  }

  private toggleSwitchView() {
    const state = this.inputTarget.checked;
    this.toggleTargetElementClasses(
      this.switchTarget,
      state,
      this.switchOffClasses,
      this.switchOnClasses,
    );
    this.toggleTargetElementClasses(
      this.sliderTarget,
      state,
      this.sliderOffClasses,
      this.sliderOnClasses,
    );
    this.toggleTargetElementClasses(
      this.sliderStartTarget,
      state,
      this.sliderStartOffClasses,
      this.sliderStartOnClasses,
    );

    this.toggleTargetElementClasses(
      this.sliderEndTarget,
      state,
      this.sliderEndOffClasses,
      this.sliderEndOnClasses,
    );
  }
}
